/**
 * **The address contract of the outstanding-work screen, and the whole of it** — the shape
 * `pages/Interview/interviewUrls.ts` established and `candidateFilters.ts` before it.
 *
 * ```
 * /outstanding?page=2
 * ```
 *
 * ⚠️ **This is the one module that spells the address.** `routes.tsx` mounts it and §6.2 Section 1's
 * *View all →* links to it (TASK-151); neither spells `/outstanding` out. The two must agree or the
 * link falls through to the catch-all, which `dashboardLinks.test.tsx`'s `href` walk is what catches.
 *
 * ⚠️ **`?page=` is in the address rather than in component state**, which is §6.3's decided shape
 * reused rather than re-invented: a page is linkable, a reload keeps it, and the back button walks
 * back a page instead of leaving the screen. It is also why the page number is **read** here and
 * validated in one place — a hand-typed `?page=ada` must not reach the API as `NaN`.
 */

/** The screen's own address, so a caller building a link does not restate it. */
export const OUTSTANDING_PATH = '/outstanding';

/** The one parameter, as data: read and written through this constant, so a rename is one edit. */
export const OUTSTANDING_PAGE_PARAM = 'page';

/**
 * The page in the address bar, defaulted and validated.
 *
 * Anything that is not a whole number of at least 1 is page 1 — `?page=ada`, `?page=0` and `?page=-3`
 * are all somebody typing rather than the screen linking, and `?page=NaN` on the wire would be a 422
 * dressed up as an error state. **A page past the end is deliberately not clamped**: the API answers it
 * with an empty page and the full count (backend TASK-156), which is a real state the screen renders,
 * and clamping it from the response is how §6.3's *Next* button walked to page 100 (`code-reviewer`,
 * 2026-07-28).
 */
export function readOutstandingPage(params: URLSearchParams): number {
  const raw = params.get(OUTSTANDING_PAGE_PARAM);
  if (raw === null) return 1;

  const page = Number(raw);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

/** The address of one page — what *Previous* and *Next* navigate to, and what a link may share. */
export function outstandingUrl(page: number): string {
  return page <= 1
    ? OUTSTANDING_PATH
    : `${OUTSTANDING_PATH}?${OUTSTANDING_PAGE_PARAM}=${String(page)}`;
}
